import * as Sentry from '@sentry/react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { createContext, JSX, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { auth } from '@/components/auth/firebase';

export const GOOGLE_ID_TOKEN_STORAGE_KEY = 'googleAuthToken';
export const ORIGINAL_PATH_STORAGE_KEY = 'original_path';

export const shouldKeepOriginalPath = (pathname: string) => !pathname.startsWith('/sign-in');

export const AuthContext = createContext<{ user: User | null; token: string | null; isAuthenticating: boolean }>({
  user: null,
  token: null,
  isAuthenticating: true
});

export const AuthProvider = ({ children }: { children: JSX.Element | JSX.Element[] }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(true);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const idToken = await firebaseUser.getIdToken();

        Sentry.setUser({ email: firebaseUser.email || 'unknown' });

        localStorage.setItem(GOOGLE_ID_TOKEN_STORAGE_KEY, idToken);
        setToken(idToken);
        setIsAuthenticating(false);
      } else {
        setUser(null);

        localStorage.removeItem(GOOGLE_ID_TOKEN_STORAGE_KEY);
        setToken(null);
        setIsAuthenticating(true);

        if (shouldKeepOriginalPath(pathname)) {
          sessionStorage.setItem(ORIGINAL_PATH_STORAGE_KEY, pathname);
        }

        navigate('/sign-in');
      }
    });
    return unsubscribe;
  }, []);

  // Refresh token every 30 minutes.
  useEffect(() => {
    const interval = setInterval(
      async () => {
        if (user) {
          // Force refresh the token.
          const newIdToken = await user.getIdToken(true);

          localStorage.setItem(GOOGLE_ID_TOKEN_STORAGE_KEY, newIdToken);
          setToken(newIdToken);
          setIsAuthenticating(false);
        }
      },
      30 * 60 * 1000
    );

    return () => clearInterval(interval);
  }, [user]);

  return <AuthContext.Provider value={{ user, token, isAuthenticating }}>{children}</AuthContext.Provider>;
};
